/**
 * useSubmitCritiqueGraphQL Hook
 *
 * GraphQL-based hook for submitting critiques with edit support.
 * Replaces the TanStack Query version with pure Apollo Client.
 *
 * Feature: 065-graphql-migration-cleanup
 */

import { useState, useCallback } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { toast } from 'sonner';
import { SubmitCritiqueFormData, ReplyFormData } from '../../types/critique';
import { parseMarkupBlocks } from '../../utils/markupBlockProcessor';
import { parseEditBlocks } from '../../utils/editBlockProcessor';
import { serializeEditToMarkdown, serializeMarkupToMarkdown } from '../../utils/editBlockSerializer';
import logger from '../../utils/logger';
import type { CropData, LightEditData, CombinedEditData } from '../../types/edit.types';

// GraphQL mutation for creating critiques
const CREATE_CRITIQUE_MUTATION = gql`
  mutation CreateCritique($input: CreateCritiqueInput!) {
    createCritique(input: $input)
  }
`;

// Feature 069 Phase 8: CREATE_CRITIQUE_EDIT_MUTATION removed
// Edits are now serialized as markdown blocks in the critique content
// and regenerated on read via editBlockSynthesizer (no DB writes)

interface UseSubmitCritiqueReturn {
  mutateAsync: (data: SubmitCritiqueFormData | ReplyFormData) => Promise<any>;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

export function useSubmitCritiqueGraphQL(
  photoId: string,
  collectionId?: string,
  sessionId?: string
): UseSubmitCritiqueReturn {
  const apolloClient = useApolloClient();
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = useCallback(
    async (data: SubmitCritiqueFormData | ReplyFormData) => {
      setIsPending(true);
      setIsError(false);
      setError(null);

      try {
        // Feature 069 Phase 8: Serialize visual editor data to markdown blocks
        let contentWithBlocks = data.content;

        // Serialize edit data (crop/light) to markdown block if provided
        if ('editType' in data && data.editType && 'editData' in data && data.editData) {
          logger.info('useSubmitCritiqueGraphQL', 'Serializing edit data to markdown block', {
            editType: data.editType,
          });

          // Convert editData to appropriate format for serializer
          let editBlock = '';
          if (data.editType === 'crop') {
            editBlock = serializeEditToMarkdown({ crop: data.editData as CropData });
          } else if (data.editType === 'light') {
            editBlock = serializeEditToMarkdown({ light: data.editData as LightEditData });
          } else if (data.editType === 'combined') {
            editBlock = serializeEditToMarkdown(data.editData as CombinedEditData);
          }

          if (editBlock) {
            contentWithBlocks = contentWithBlocks + '\n\n' + editBlock;
          }
        }

        // Serialize markup data to markdown blocks if provided
        if ('markupData' in data && data.markupData && data.markupData.length > 0) {
          logger.info('useSubmitCritiqueGraphQL', 'Serializing markup data to markdown blocks', {
            count: data.markupData.length,
          });

          for (const markup of data.markupData) {
            if (markup.shapes && markup.shapes.length > 0) {
              const markupBlock = serializeMarkupToMarkdown(markup.shapes);
              if (markupBlock) {
                contentWithBlocks = contentWithBlocks + '\n\n' + markupBlock;
              }
            }
          }
        }

        // Parse markup blocks (Feature 069: always-on processing)
        // This validates the serialized blocks we just added
        logger.info('useSubmitCritiqueGraphQL', 'Processing markup blocks');
        const markupResult = parseMarkupBlocks(contentWithBlocks);

        // Parse edit blocks (crop/light) from the cleaned markdown
        logger.info('useSubmitCritiqueGraphQL', 'Processing edit blocks');
        const editResult = parseEditBlocks(markupResult.cleanedMarkdown);

        // Collect all parsing errors
        const allErrors = [...markupResult.errors, ...editResult.errors];

        if (allErrors.length > 0) {
          logger.warn('useSubmitCritiqueGraphQL', 'Block parsing errors', {
            errors: allErrors,
          });
          toast.warning('Some blocks had issues', {
            description:
              allErrors.slice(0, 3).join('\n') +
              (allErrors.length > 3
                ? `\n...and ${allErrors.length - 3} more`
                : ''),
          });
        }

        // Use original content with serialized blocks (NOT the cleaned version)
        // The blocks stay in the markdown for read-path regeneration
        const processedContent = contentWithBlocks;

        // Build GraphQL input for critique
        const input: {
          photoId: string;
          content: string;
          collectionId?: string;
          sessionId?: string;
          parentId?: string;
          editId?: string;
        } = {
          photoId,
          content: processedContent,
        };

        if (collectionId) input.collectionId = collectionId;
        if (sessionId) input.sessionId = sessionId;
        if ('parentId' in data && data.parentId) input.parentId = data.parentId;
        if ('editId' in data && data.editId) input.editId = String(data.editId);

        // Call GraphQL mutation (don't refetch yet - wait until all edits are created)
        logger.info('useSubmitCritiqueGraphQL', 'Creating critique');
        const result = await apolloClient.mutate({
          mutation: CREATE_CRITIQUE_MUTATION,
          variables: { input },
        });

        if (result.errors) {
          throw new Error(result.errors[0]?.message || 'Failed to create critique');
        }

        const newCritique = result.data?.createCritique;

        // Feature 069 Phase 8: No separate edit mutations needed
        // All edits (crop, light, markup) are serialized as markdown blocks
        // and will be regenerated on read by editBlockSynthesizer
        logger.info('useSubmitCritiqueGraphQL', 'Critique created with markdown blocks', {
          critiqueId: newCritique?.id,
          hasEditData: 'editType' in data && !!data.editType,
          hasMarkupData: 'markupData' in data && !!data.markupData?.length,
        });

        // Refetch queries to update UI
        // Feature 071: Also refetch session to update AI feedback status
        await apolloClient.refetchQueries({
          include: ['GetCritiquesForCollection', 'GetCritiquesForPhoto', 'GetCritiqueSession'],
        });

        setIsPending(false);
        return newCritique;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to submit critique');
        setError(error);
        setIsError(true);
        setIsPending(false);
        throw error;
      }
    },
    [apolloClient, photoId, collectionId, sessionId]
  );

  return {
    mutateAsync,
    isPending,
    isError,
    error,
  };
}
